import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  Avatar,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Badge,
  Stack,
  Menu as MuiMenu,
  ListItemIcon,
  ListItemText,
  Alert
} from '@mui/material';
import {
  Send,
  Add,
  Group,
  MoreVert,
  Circle,
  Chat as ChatIcon,
  PersonRemove,
  DeleteForever,
  Delete,
  VolumeOff,
  PushPin
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';
import { ChatGroup, ChatMessage, ChatUser, UserRole } from '../types';
import dataConnectionService from '../services/dataConnectionService';

const Chat: React.FC = () => {
  const { user, hasRole } = useAuth();
  const { mode } = useCustomTheme();
  const [groups, setGroups] = useState<ChatGroup[]>([]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<ChatGroup | null>(null);
  const [newMessage, setNewMessage] = useState('');
  const [isCreateGroupOpen, setIsCreateGroupOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDescription, setNewGroupDescription] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedGroupForMenu, setSelectedGroupForMenu] = useState<ChatGroup | null>(null);
  const [isRemoveUserOpen, setIsRemoveUserOpen] = useState(false);
  const [isTransferAdminOpen, setIsTransferAdminOpen] = useState(false);
  const [usersToRemove, setUsersToRemove] = useState<string[]>([]);
  const [newAdminId, setNewAdminId] = useState<string>('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Mock users data
  const allUsers: ChatUser[] = [
    { id: '1', name: 'Ahmet Yılmaz', role: UserRole.ADMIN, isOnline: true, lastSeen: new Date() },
    { id: '2', name: 'Fatma Demir', role: UserRole.CALL_ADMIN, isOnline: true, lastSeen: new Date() },
    { id: '3', name: 'Mehmet Kaya', role: UserRole.CALL, isOnline: false, lastSeen: new Date(Date.now() - 300000) },
    { id: '4', name: 'Ayşe Özkan', role: UserRole.LIVECHAT_ADMIN, isOnline: true, lastSeen: new Date() },
    { id: '5', name: 'Ali Veli', role: UserRole.LIVECHAT, isOnline: false, lastSeen: new Date(Date.now() - 600000) },
    { id: '6', name: 'Zeynep Ak', role: UserRole.RISK_ADMIN, isOnline: true, lastSeen: new Date() },
    { id: '7', name: 'Can Yılmaz', role: UserRole.RISK, isOnline: true, lastSeen: new Date() }
  ];

  // Data connection durumunu kontrol et
  const isDataConnected = dataConnectionService.isConnected();
  
  // Mock groups data (sadece data bağlantısı yoksa)
  useEffect(() => {
    if (isDataConnected) {
      // Gerçek data'dan grupları yükle
      setGroups([]);
      return;
    }
    
    const mockGroups: ChatGroup[] = [
      {
        id: '1',
        name: 'Genel Sohbet',
        description: 'Tüm ekip için genel sohbet',
        createdBy: '1',
        createdAt: new Date(),
        members: ['1', '2', '3', '4', '5', '6', '7'],
        isActive: true,
        isPinned: true,
        isMuted: false,
        admins: ['1']
      },
      {
        id: '2',
        name: 'Call Ekibi',
        description: 'Call ekibi için özel grup',
        createdBy: '1',
        createdAt: new Date(),
        members: ['1', '2', '3'],
        isActive: true,
        isPinned: false,
        isMuted: false,
        admins: ['1']
      },
      {
        id: '3',
        name: 'Risk Yönetimi',
        description: 'Risk ekibi için özel grup',
        createdBy: '1',
        createdAt: new Date(),
        members: ['1', '6', '7'],
        isActive: true,
        isPinned: false,
        isMuted: false,
        admins: ['1']
      }
    ];
    setGroups(mockGroups);
  }, [isDataConnected]);

  // Mock messages data
  useEffect(() => {
    if (selectedGroup) {
      const mockMessages: ChatMessage[] = [
        {
          id: '1',
          groupId: selectedGroup.id,
          userId: '1',
          userName: 'Ahmet Yılmaz',
          userRole: UserRole.ADMIN,
          content: 'Merhaba ekip! Bugün nasıl gidiyor?',
          timestamp: new Date(Date.now() - 3600000),
          type: 'text'
        },
        {
          id: '2',
          groupId: selectedGroup.id,
          userId: '2',
          userName: 'Fatma Demir',
          userRole: UserRole.CALL_ADMIN,
          content: 'Merhaba Ahmet! Bugün çok yoğun bir gün geçiriyoruz.',
          timestamp: new Date(Date.now() - 3500000),
          type: 'text'
        },
        {
          id: '3',
          groupId: selectedGroup.id,
          userId: '3',
          userName: 'Mehmet Kaya',
          userRole: UserRole.CALL,
          content: 'Evet, aynen. Çok fazla arama var.',
          timestamp: new Date(Date.now() - 3400000),
          type: 'text'
        }
      ];
      setMessages(mockMessages);
    }
  }, [selectedGroup]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Rol kontrolü - tüm kullanıcılar erişebilir
  if (!hasRole(['admin', 'developer', 'mod', 'call_admin', 'call', 'livechat_admin', 'livechat', 'risk_admin', 'risk'])) {
    return (
      <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
        <Alert severity="error" sx={{ mt: 4 }}>
          Bu sayfaya erişim yetkiniz bulunmamaktadır.
        </Alert>
      </Container>
    );
  }

  const isAdmin = user?.role === UserRole.ADMIN;

  // Grup yönetimi fonksiyonları
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, group: ChatGroup) => {
    setAnchorEl(event.currentTarget);
    setSelectedGroupForMenu(group);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedGroupForMenu(null);
  };

  const handleRemoveUser = () => {
    if (selectedGroupForMenu && usersToRemove.length > 0) {
      setGroups(prev => prev.map(group => 
        group.id === selectedGroupForMenu.id 
          ? { ...group, members: group.members.filter(id => !usersToRemove.includes(id)) }
          : group
      ));
      
      // Sistem mesajı ekle
      const systemMessage: ChatMessage = {
        id: Date.now().toString(),
        groupId: selectedGroupForMenu.id,
        userId: 'system',
        userName: 'Sistem',
        userRole: UserRole.ADMIN,
        content: `${usersToRemove.map(id => allUsers.find(u => u.id === id)?.name).join(', ')} gruptan çıkarıldı.`,
        timestamp: new Date(),
        type: 'system'
      };
      setMessages(prev => [...prev, systemMessage]);
      
      setUsersToRemove([]);
      setIsRemoveUserOpen(false);
      handleMenuClose();
    }
  };

  const handleDisbandGroup = () => {
    if (selectedGroupForMenu) {
      setGroups(prev => prev.filter(group => group.id !== selectedGroupForMenu.id));
      if (selectedGroup?.id === selectedGroupForMenu.id) {
        setSelectedGroup(null);
      }
      handleMenuClose();
    }
  };

  const handleDeleteGroup = () => {
    if (selectedGroupForMenu && newAdminId) {
      setGroups(prev => prev.map(group => 
        group.id === selectedGroupForMenu.id 
          ? { ...group, admins: [newAdminId], createdBy: newAdminId }
          : group
      ));
      setNewAdminId('');
      setIsTransferAdminOpen(false);
      handleMenuClose();
    }
  };

  const handleToggleMute = () => {
    if (selectedGroupForMenu) {
      setGroups(prev => prev.map(group => 
        group.id === selectedGroupForMenu.id 
          ? { ...group, isMuted: !group.isMuted }
          : group
      ));
      handleMenuClose();
    }
  };

  const handleTogglePin = () => {
    if (selectedGroupForMenu) {
      setGroups(prev => prev.map(group => 
        group.id === selectedGroupForMenu.id 
          ? { ...group, isPinned: !group.isPinned }
          : group
      ));
      handleMenuClose();
    }
  };

  const handleSendMessage = () => {
    if (newMessage.trim() && selectedGroup && user) {
      const message: ChatMessage = {
        id: Date.now().toString(),
        groupId: selectedGroup.id,
        userId: user.id,
        userName: `${user.firstName} ${user.lastName}`,
        userRole: user.role,
        content: newMessage.trim(),
        timestamp: new Date(),
        type: 'text'
      };
      setMessages(prev => [...prev, message]);
      setNewMessage('');
    }
  };

  const handleCreateGroup = () => {
    if (newGroupName.trim() && user) {
      const group: ChatGroup = {
        id: Date.now().toString(),
        name: newGroupName.trim(),
        description: newGroupDescription.trim(),
        createdBy: user.id,
        createdAt: new Date(),
        members: selectedUsers,
        isActive: true,
        isPinned: false,
        isMuted: false,
        admins: [user.id]
      };
      setGroups(prev => [...prev, group]);
      setNewGroupName('');
      setNewGroupDescription('');
      setSelectedUsers([]);
      setIsCreateGroupOpen(false);
    }
  };

  const handleUserToggle = (userId: string) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const getRoleColor = (role: UserRole) => {
    const colors = {
      [UserRole.ADMIN]: 'error',
      [UserRole.DEVELOPER]: 'primary',
      [UserRole.MOD]: 'secondary',
      [UserRole.CALL_ADMIN]: 'warning',
      [UserRole.CALL]: 'info',
      [UserRole.LIVECHAT_ADMIN]: 'warning',
      [UserRole.LIVECHAT]: 'info',
      [UserRole.RISK_ADMIN]: 'warning',
      [UserRole.RISK]: 'info'
    };
    return colors[role] as any;
  };

  const getRoleDisplayName = (role: UserRole): string => {
    const roleNames = {
      [UserRole.ADMIN]: 'Admin',
      [UserRole.DEVELOPER]: 'Developer',
      [UserRole.MOD]: 'Mod',
      [UserRole.CALL_ADMIN]: 'Call Admin',
      [UserRole.CALL]: 'Call',
      [UserRole.LIVECHAT_ADMIN]: 'Canlı Chat Admin',
      [UserRole.LIVECHAT]: 'Canlı Chat',
      [UserRole.RISK_ADMIN]: 'Risk Admin',
      [UserRole.RISK]: 'Risk'
    };
    return roleNames[role] || role;
  };

  const formatTime = (date: Date) => {
    return new Intl.DateTimeFormat('tr-TR', {
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const getOnlineMembers = (group: ChatGroup) => {
    return group.members.filter(memberId => {
      const member = allUsers.find(u => u.id === memberId);
      return member?.isOnline;
    }).length;
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      {/* Header */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: 3,
        p: 3,
        mb: 3,
        color: 'white'
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              💬 Canlı Chat
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Ekip arkadaşlarınızla anlık iletişim kurun
            </Typography>
            
            {/* Data Connection Durumu */}
            {!isDataConnected && (
              <Alert severity="info" sx={{ mt: 2, bgcolor: 'rgba(255,255,255,0.1)', color: 'white' }}>
                <Typography variant="body2">
                  <strong>Mock Veri Modu:</strong> Data bağlantısı kurulmadığı için örnek chat verileri gösteriliyor. 
                  Gerçek veriler için Sayfa Ayarları'ndan data bağlantısı kurun.
                </Typography>
              </Alert>
            )}
          </Box>
          {isAdmin && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setIsCreateGroupOpen(true)}
              sx={{
                background: 'rgba(255,255,255,0.2)',
                color: 'white',
                '&:hover': {
                  background: 'rgba(255,255,255,0.3)'
                }
              }}
            >
              Grup Oluştur
            </Button>
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '300px 1fr' }, gap: 3, height: '70vh' }}>
        {/* Grup Listesi */}
        <Card sx={{ height: '100%', borderRadius: 3 }}>
          <CardContent sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h6" fontWeight="bold">
                Gruplar ({groups.length})
              </Typography>
            </Box>
            
            <List sx={{ flex: 1, overflow: 'auto' }}>
              {groups
                .sort((a, b) => {
                  if (a.isPinned && !b.isPinned) return -1;
                  if (!a.isPinned && b.isPinned) return 1;
                  return 0;
                })
                .map((group) => (
                <ListItem key={group.id} disablePadding>
                  <ListItemButton
                    selected={selectedGroup?.id === group.id}
                    onClick={() => setSelectedGroup(group)}
                    sx={{
                      '&.Mui-selected': {
                        backgroundColor: 'primary.main',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'primary.dark',
                        },
                      },
                      '&:not(.Mui-selected)': {
                        color: mode === 'dark' ? 'text.primary' : 'text.primary',
                        '&:hover': {
                          backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'action.hover',
                        },
                      },
                    }}
                  >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      <Group />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography component="span" variant="subtitle1" fontWeight="bold">
                          {group.name}
                        </Typography>
                        <Badge 
                          badgeContent={getOnlineMembers(group)} 
                          color="success"
                        />
                      </Box>
                    }
                    primaryTypographyProps={{ component: 'div' }}
                    secondary={
                      <Box>
                        <Typography component="span" variant="body2" color="text.secondary" noWrap>
                          {group.description}
                        </Typography>
                        <Typography component="span" variant="caption" color="text.secondary">
                          {group.members.length} üye
                        </Typography>
                      </Box>
                    }
                    secondaryTypographyProps={{ component: 'div' }}
                  />
                  {isAdmin && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMenuClick(e, group);
                      }}
                    >
                      <MoreVert />
                    </IconButton>
                  )}
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>

        {/* Chat Alanı */}
        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 3 }}>
          {selectedGroup ? (
            <Box>
              {/* Chat Header */}
              <Box sx={{ 
                p: 2, 
                borderBottom: 1, 
                borderColor: 'divider',
                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                color: 'white'
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      {selectedGroup.name}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      {selectedGroup.description}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Badge 
                      badgeContent={getOnlineMembers(selectedGroup)} 
                      color="success"
                    >
                      <Circle sx={{ color: 'success.main' }} />
                    </Badge>
                    <Typography variant="body2">
                      {getOnlineMembers(selectedGroup)} online
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Mesajlar */}
              <Box sx={{ 
                flex: 1, 
                overflow: 'auto', 
                p: 2,
                background: mode === 'dark' 
                  ? 'linear-gradient(135deg, #2d3748 0%, #1a202c 100%)'
                  : 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
              }}>
                <Stack spacing={2}>
                  {messages.map((message) => (
                    <Box
                      key={message.id}
                      sx={{
                        display: 'flex',
                        justifyContent: message.userId === user?.id ? 'flex-end' : 'flex-start',
                        mb: 1
                      }}
                    >
                      <Box
                        sx={{
                          maxWidth: '70%',
                          p: 2,
                          borderRadius: 2,
                          background: message.userId === user?.id 
                            ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                            : mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'white',
                          color: message.userId === user?.id 
                            ? 'white' 
                            : mode === 'dark' ? 'text.primary' : 'text.primary',
                          boxShadow: mode === 'dark' ? 2 : 1,
                          border: mode === 'dark' ? '1px solid rgba(255,255,255,0.1)' : 'none'
                        }}
                      >
                        {message.userId !== user?.id && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Typography variant="caption" fontWeight="bold">
                              {message.userName}
                            </Typography>
                            <Chip 
                              label={getRoleDisplayName(message.userRole)} 
                              size="small" 
                              color={getRoleColor(message.userRole)}
                              sx={{ fontSize: '0.7rem', height: 20 }}
                            />
                          </Box>
                        )}
                        <Typography variant="body2">
                          {message.content}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            display: 'block', 
                            mt: 1, 
                            opacity: 0.7,
                            textAlign: 'right'
                          }}
                        >
                          {formatTime(message.timestamp)}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                  <div ref={messagesEndRef} />
                </Stack>
              </Box>

              {/* Mesaj Gönderme */}
              <Box sx={{ 
                p: 2, 
                borderTop: 1, 
                borderColor: 'divider',
                backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'transparent'
              }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    fullWidth
                    placeholder="Mesajınızı yazın..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    multiline
                    maxRows={3}
                    variant="outlined"
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'white',
                        '& fieldset': {
                          borderColor: mode === 'dark' ? 'rgba(255,255,255,0.3)' : undefined,
                        },
                        '&:hover fieldset': {
                          borderColor: mode === 'dark' ? 'rgba(255,255,255,0.5)' : undefined,
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: mode === 'dark' ? 'primary.main' : undefined,
                        },
                      },
                      '& .MuiInputBase-input': {
                        color: mode === 'dark' ? 'white' : 'text.primary',
                        '&::placeholder': {
                          color: mode === 'dark' ? 'rgba(255,255,255,0.6)' : 'text.secondary',
                        },
                      },
                    }}
                  />
                  <IconButton
                    color="primary"
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim()}
                    sx={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                      },
                      '&:disabled': {
                        background: 'grey.300',
                        color: 'grey.500'
                      }
                    }}
                  >
                    <Send />
                  </IconButton>
                </Box>
              </Box>
            </Box>
          ) : (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center', 
              height: '100%',
              p: 4
            }}>
              <ChatIcon sx={{ fontSize: 80, color: 'grey.400', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Bir grup seçin
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Sohbet etmek için sol taraftan bir grup seçin
              </Typography>
            </Box>
          )}
        </Card>
      </Box>

      {/* Grup Oluşturma Dialog */}
      <Dialog open={isCreateGroupOpen} onClose={() => setIsCreateGroupOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6" fontWeight="bold">
            🆕 Yeni Grup Oluştur
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Grup Adı"
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              placeholder="Örn: Call Ekibi"
            />
            <TextField
              fullWidth
              label="Grup Açıklaması"
              value={newGroupDescription}
              onChange={(e) => setNewGroupDescription(e.target.value)}
              placeholder="Grup hakkında kısa açıklama"
              multiline
              rows={2}
            />
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Üyeleri Seçin
              </Typography>
              <List sx={{ maxHeight: 200, overflow: 'auto' }}>
                {allUsers.filter(u => u.id !== user?.id).map((user) => (
                  <ListItem key={user.id} dense>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedUsers.includes(user.id)}
                          onChange={() => handleUserToggle(user.id)}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 24, height: 24 }}>
                            {user.name[0]}
                          </Avatar>
                          <Typography variant="body2">
                            {user.name}
                          </Typography>
                          <Chip 
                            label={getRoleDisplayName(user.role)} 
                            size="small" 
                            color={getRoleColor(user.role)}
                            sx={{ fontSize: '0.7rem', height: 20 }}
                          />
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsCreateGroupOpen(false)}>
            İptal
          </Button>
          <Button 
            onClick={handleCreateGroup} 
            variant="contained"
            disabled={!newGroupName.trim() || selectedUsers.length === 0}
          >
            Grup Oluştur
          </Button>
        </DialogActions>
      </Dialog>

      {/* Grup Yönetimi Menüsü */}
      <MuiMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: { minWidth: 200 }
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={() => setIsRemoveUserOpen(true)}>
          <ListItemIcon>
            <PersonRemove fontSize="small" />
          </ListItemIcon>
          <ListItemText>Kullanıcı Çıkar</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={handleDisbandGroup}>
          <ListItemIcon>
            <DeleteForever fontSize="small" />
          </ListItemIcon>
          <ListItemText>Grubu Dağıt</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={() => setIsTransferAdminOpen(true)}>
          <ListItemIcon>
            <Delete fontSize="small" />
          </ListItemIcon>
          <ListItemText>Grubu Sil</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={handleToggleMute}>
          <ListItemIcon>
            <VolumeOff fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            {selectedGroupForMenu?.isMuted ? 'Sessizi Kaldır' : 'Sessize Al'}
          </ListItemText>
        </MenuItem>
        
        <MenuItem onClick={handleTogglePin}>
          <ListItemIcon>
            <PushPin fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            {selectedGroupForMenu?.isPinned ? 'Sabitlemeyi Kaldır' : 'Başa Tuttur'}
          </ListItemText>
        </MenuItem>
      </MuiMenu>

      {/* Kullanıcı Çıkarma Dialog */}
      <Dialog open={isRemoveUserOpen} onClose={() => setIsRemoveUserOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6" fontWeight="bold">
            Kullanıcı Çıkar
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Gruptan çıkarmak istediğiniz kullanıcıları seçin:
          </Typography>
          <List>
            {selectedGroupForMenu?.members
              .filter(memberId => memberId !== user?.id)
              .map((memberId) => {
                const member = allUsers.find(u => u.id === memberId);
                return member ? (
                  <ListItem key={memberId} dense>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={usersToRemove.includes(memberId)}
                          onChange={() => {
                            if (usersToRemove.includes(memberId)) {
                              setUsersToRemove(prev => prev.filter(id => id !== memberId));
                            } else {
                              setUsersToRemove(prev => [...prev, memberId]);
                            }
                          }}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 24, height: 24 }}>
                            {member.name[0]}
                          </Avatar>
                          <Typography variant="body2">
                            {member.name}
                          </Typography>
                          <Chip 
                            label={getRoleDisplayName(member.role)} 
                            size="small" 
                            color={getRoleColor(member.role)}
                            sx={{ fontSize: '0.7rem', height: 20 }}
                          />
                        </Box>
                      }
                    />
                  </ListItem>
                ) : null;
              })}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsRemoveUserOpen(false)}>
            İptal
          </Button>
          <Button 
            onClick={handleRemoveUser} 
            variant="contained"
            disabled={usersToRemove.length === 0}
            color="error"
          >
            Çıkar ({usersToRemove.length})
          </Button>
        </DialogActions>
      </Dialog>

      {/* Yönetici Transfer Dialog */}
      <Dialog open={isTransferAdminOpen} onClose={() => setIsTransferAdminOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6" fontWeight="bold">
            Grubu Sil
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Grubu silmek için yöneticiliği başka bir kullanıcıya transfer etmelisiniz:
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Yeni Yönetici</InputLabel>
            <Select
              value={newAdminId}
              onChange={(e) => setNewAdminId(e.target.value)}
              label="Yeni Yönetici"
            >
              {selectedGroupForMenu?.members
                .filter(memberId => memberId !== user?.id)
                .map((memberId) => {
                  const member = allUsers.find(u => u.id === memberId);
                  return member ? (
                    <MenuItem key={memberId} value={memberId}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 24, height: 24 }}>
                          {member.name[0]}
                        </Avatar>
                        <Typography variant="body2">
                          {member.name}
                        </Typography>
                        <Chip 
                          label={getRoleDisplayName(member.role)} 
                          size="small" 
                          color={getRoleColor(member.role)}
                          sx={{ fontSize: '0.7rem', height: 20 }}
                        />
                      </Box>
                    </MenuItem>
                  ) : null;
                })}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsTransferAdminOpen(false)}>
            İptal
          </Button>
          <Button 
            onClick={handleDeleteGroup} 
            variant="contained"
            disabled={!newAdminId}
            color="error"
          >
            Transfer Et ve Sil
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Chat;
